import {
	AnimatePresence,
	type MotionProps,
	motion,
	useReducedMotion,
} from "motion/react";
import React, {
	type ComponentPropsWithoutRef,
	useEffect,
	useMemo,
	useState,
} from "react";
import { cn } from "utils/cn";

export function AnimatedListItem({ children }: { children: React.ReactNode }) {
	// Scaling each row in from zero is decoration, not information. Under
	// prefers-reduced-motion the row still has to appear — it just appears
	// without the spring, and without `layout` animating its position after.
	// Same gate every other animated surface here uses (components/polish/*,
	// AppSider, and the ShineBorder beside this list via `motion-safe:`).
	const reducedMotion = useReducedMotion();
	const animations: MotionProps = reducedMotion
		? {
				initial: { opacity: 1 },
				animate: { opacity: 1 },
				exit: { opacity: 1 },
				transition: { duration: 0 },
			}
		: {
				initial: { scale: 0, opacity: 0 },
				animate: { scale: 1, opacity: 1, originY: 0 },
				exit: { scale: 0, opacity: 0 },
				transition: { type: "spring", stiffness: 350, damping: 40 },
			};

	return (
		<motion.div
			{...animations}
			layout={!reducedMotion}
			className="mx-auto w-full"
		>
			{children}
		</motion.div>
	);
}

export interface AnimatedListProps extends ComponentPropsWithoutRef<"div"> {
	children: React.ReactNode;
	delay?: number;
}

export const AnimatedList = React.memo(
	({ children, className, delay = 1000, ...props }: AnimatedListProps) => {
		const [index, setIndex] = useState(0);
		const childrenArray = useMemo(
			() => React.Children.toArray(children),
			[children],
		);

		useEffect(() => {
			let timeout: ReturnType<typeof setTimeout> | null = null;

			if (index < childrenArray.length - 1) {
				timeout = setTimeout(() => {
					setIndex((prevIndex) => (prevIndex + 1) % childrenArray.length);
				}, delay);
			}

			return () => {
				if (timeout !== null) {
					clearTimeout(timeout);
				}
			};
		}, [index, delay, childrenArray.length]);

		const itemsToShow = useMemo(() => {
			const result = childrenArray.slice(0, index + 1).reverse();
			return result;
		}, [index, childrenArray]);

		return (
			<div
				className={cn("flex flex-col items-center gap-4", className)}
				{...props}
			>
				<AnimatePresence>
					{itemsToShow.map((item) => (
						<AnimatedListItem key={(item as React.ReactElement).key}>
							{item}
						</AnimatedListItem>
					))}
				</AnimatePresence>
			</div>
		);
	},
);

AnimatedList.displayName = "AnimatedList";
